// Carry Roll Input slice — owns the canonical-SI forward-model request.

import type { StateCreator } from 'zustand';
import type {
  ClubContext,
  DeliveryInputs,
  EnvironmentInputs,
  LandingOverride,
  LaunchInputs,
  SimulationRequest,
} from '@carry-roll/shared-types';
import { ClubCategory } from '@carry-roll/shared-types';
import type { BallType } from '@carry-roll/shared-logic';
import { DEFAULT_DRIVER_REQUEST } from '@carry-roll/shared-logic';

export interface InputSlice {
  request: SimulationRequest;
  setLaunch: (patch: Partial<LaunchInputs>) => void;
  setDelivery: (patch: Partial<DeliveryInputs>) => void;
  setEnvironment: (patch: Partial<EnvironmentInputs>) => void;
  setOverride: (patch: Partial<LandingOverride>) => void;
  setClub: (patch: Partial<ClubContext> | null) => void;
  setBallType: (bt: BallType) => void;
  resetInputs: () => void;
  loadProfile: (req: SimulationRequest) => void;
}

export const createInputSlice: StateCreator<InputSlice, [], [], InputSlice> = (set) => ({
  request: structuredClone(DEFAULT_DRIVER_REQUEST),
  setLaunch: (patch) =>
    set((state) => ({ request: { ...state.request, launch: { ...state.request.launch, ...patch } } })),
  setDelivery: (patch) =>
    set((state) => ({
      request: {
        ...state.request,
        delivery: state.request.delivery
          ? { ...state.request.delivery, ...patch }
          : { attackAngleDeg: null, dynamicLoftDeg: null, clubPathDeg: null, faceAngleDeg: null, impactMm: null, lieAngleDeg: null, ...patch },
      },
    })),
  setEnvironment: (patch) =>
    set((state) => ({
      request: { ...state.request, environment: { ...state.request.environment, ...patch } },
    })),
  setOverride: (patch) =>
    set((state) => ({ request: { ...state.request, override: { ...state.request.override, ...patch } } })),
  setClub: (patch) =>
    set((state) => ({
      request: {
        ...state.request,
        club: patch == null
          ? null
          : {
              ...(state.request.club ?? {
                category: ClubCategory.Driver,
                loftDeg: null,
                rollPercent: null,
              }),
              ...patch,
            },
      },
    })),
  resetInputs: () => set({ request: structuredClone(DEFAULT_DRIVER_REQUEST) }),
  loadProfile: (req) => set({ request: structuredClone(req) }),
  setBallType: (bt) => set((state) => ({ request: { ...state.request, ballType: bt } })),
});
